import { apple, type Keys, keysOf, type Stroke } from "./keys.ts"

/*
 * Every keyboard shortcut in the web app, in one place: the listener matches presses against it,
 * tooltips and `Kbd`s show its keys, and the Shortcuts dialog lists it. A page binds an entry to
 * what it does with `useShortcut`; nothing presses a key the registry doesn't name.
 *
 * Several entries share keys on purpose — C creates whatever the page it is pressed on makes —
 * since no two of them are mounted at once. `registry.test.ts` keeps any that could be from
 * clashing.
 */

/** Where a shortcut works, as the Shortcuts dialog groups them. */
export type Scope =
  | "global"
  | "go-to"
  | "lists"
  | "filters"
  | "forms"
  | "board"
  | "projects"
  | "groups"
  | "ticket"
  | "spend"
  | "project-settings"

/** Each scope's heading in the Shortcuts dialog, in the order the dialog lists them. */
export const scopes: Record<Scope, string> = {
  global: "General",
  "go-to": "Go to",
  lists: "Lists",
  filters: "Filters and search",
  forms: "Forms",
  board: "Board",
  projects: "Projects",
  groups: "Groups",
  ticket: "Ticket",
  spend: "Spend",
  "project-settings": "Project settings",
}

export type Shortcut = {
  /** What it does, as the Shortcuts dialog and the command palette say it. */
  label: string
  scope: Scope
  /** Its keys, the first as tooltips show them, and any others that do the same (j and ↓). */
  keys: readonly [Keys, ...Keys[]]
  /** Only an Admin has it, since only an Admin can reach where it goes. */
  admin?: true
  /** It works while typing in a field, as ⌘↵ submits the form the field is in. */
  inFields?: true
}

const press = (key: string, modifiers: Omit<Stroke, "key"> = {}): Keys => [{ key, ...modifiers }]
const chord = (first: string, second: string): Keys => [{ key: first }, { key: second }]

export const shortcuts = {
  "command-palette": {
    label: "Open the command palette",
    scope: "global",
    keys: [press("k", { mod: true })],
    inFields: true,
  },
  // Not in fields: there ⌘B is bold.
  "toggle-sidebar": {
    label: "Collapse or expand the sidebar",
    scope: "global",
    keys: [press("b", { mod: true })],
  },
  shortcuts: { label: "Show keyboard shortcuts", scope: "global", keys: [press("?")] },
  // A page with a search field takes this for its field; elsewhere it opens the palette.
  search: { label: "Search", scope: "global", keys: [press("/")] },
  // An open overlay closes itself on Esc, so this is taken only when none is open.
  "clear-filters": {
    label: "Close the overlay, or clear the filters",
    scope: "global",
    keys: [press("Escape")],
  },

  "go-home": { label: "Go to Home", scope: "go-to", keys: [chord("g", "h")] },
  "go-my-tickets": { label: "Go to My tickets", scope: "go-to", keys: [chord("g", "t")] },
  "go-my-project": { label: "Go to My Project", scope: "go-to", keys: [chord("g", "o")] },
  "go-projects": { label: "Go to Projects", scope: "go-to", keys: [chord("g", "p")] },
  "go-people": { label: "Go to People", scope: "go-to", keys: [chord("g", "e")] },
  "go-profile": { label: "Go to Your Profile", scope: "go-to", keys: [chord("g", "u")] },
  "go-my-connectors": { label: "Go to My Connectors", scope: "go-to", keys: [chord("g", "c")] },
  "go-connect-an-agent": {
    label: "Go to Connect an agent",
    scope: "go-to",
    keys: [chord("g", "n")],
  },
  "go-groups": { label: "Go to Groups", scope: "go-to", keys: [chord("g", "r")], admin: true },
  "go-agent": { label: "Go to Agent", scope: "go-to", keys: [chord("g", "a")], admin: true },
  "go-spend": { label: "Go to Spend", scope: "go-to", keys: [chord("g", "s")], admin: true },
  "go-jira": { label: "Go to Jira", scope: "go-to", keys: [chord("g", "k")], admin: true },
  "go-github": { label: "Go to GitHub", scope: "go-to", keys: [chord("g", "i")], admin: true },

  "row-down": {
    label: "Move the highlight down",
    scope: "lists",
    keys: [press("j"), press("ArrowDown")],
  },
  "row-up": {
    label: "Move the highlight up",
    scope: "lists",
    keys: [press("k"), press("ArrowUp")],
  },
  "row-open": { label: "Open the highlighted row", scope: "lists", keys: [press("Enter")] },

  "open-filter": { label: "Open Filter", scope: "filters", keys: [press("f")] },

  submit: {
    label: "Submit the form, or save changes",
    scope: "forms",
    keys: [press("Enter", { mod: true })],
    inFields: true,
  },
  discard: {
    label: "Discard unsaved changes",
    scope: "forms",
    keys: [press("Escape")],
    inFields: true,
  },

  "new-ticket": { label: "New ticket", scope: "board", keys: [press("c")] },
  "new-epic": { label: "New Epic", scope: "board", keys: [press("c", { shift: true })] },

  "new-project": { label: "New Project", scope: "projects", keys: [press("c")] },

  "new-group": { label: "New group", scope: "groups", keys: [press("c")] },

  "focus-comment": { label: "Write a comment", scope: "ticket", keys: [press("c")] },
  "pick-status": { label: "Change the Status", scope: "ticket", keys: [press("s")] },
  "pick-priority": { label: "Change the priority", scope: "ticket", keys: [press("p")] },
  "pick-size": {
    label: "Change the Size",
    scope: "ticket",
    keys: [press("s", { shift: true })],
  },
  "pick-type": { label: "Change the type", scope: "ticket", keys: [press("t")] },
  "pick-assignees": { label: "Change the assignees", scope: "ticket", keys: [press("a")] },
  "pick-epic": { label: "Change the Epic", scope: "ticket", keys: [press("e")] },
  "pick-area": {
    label: "Change the Area",
    scope: "ticket",
    keys: [press("a", { shift: true })],
  },
  "pick-labels": { label: "Change the Labels", scope: "ticket", keys: [press("l")] },
  "copy-link": {
    label: "Copy the ticket's link",
    scope: "ticket",
    keys: [press(".", { mod: true })],
  },

  "range-month": { label: "Show this month", scope: "spend", keys: [press("1")] },
  "range-3m": { label: "Show 3 months", scope: "spend", keys: [press("2")] },
  "range-6m": { label: "Show 6 months", scope: "spend", keys: [press("3")] },
  "range-12m": { label: "Show 12 months", scope: "spend", keys: [press("4")] },
  "previous-day": { label: "Show the day before", scope: "spend", keys: [press("ArrowLeft")] },
  "next-day": { label: "Show the day after", scope: "spend", keys: [press("ArrowRight")] },

  // A Status's place is its column on the Board, left to right, an Area's its lane, top to bottom,
  // and a Label's its place in the Project's order; each is a row in Project settings, where these
  // keys move it: "earlier" and "later" are true everywhere.
  "move-earlier": {
    label: "Move the focused Status, Area or Label earlier",
    scope: "project-settings",
    keys: [press("ArrowUp", { alt: true })],
  },
  "move-later": {
    label: "Move the focused Status, Area or Label later",
    scope: "project-settings",
    keys: [press("ArrowDown", { alt: true })],
  },
} as const satisfies Record<string, Shortcut>

export type ShortcutId = keyof typeof shortcuts

/** One entry by its id, typed as any entry is. */
export const shortcutOf = (id: ShortcutId): Shortcut => shortcuts[id]

/** A shortcut's chips for a `Kbd`, from its first keys: a list per stroke, as `keysOf` gives. */
export const shortcutKeys = (id: ShortcutId, onApple = apple) =>
  keysOf(shortcuts[id].keys[0], onApple)

/**
 * The shortcuts a viewer has, grouped by scope in the dialog's order, each group's in the
 * registry's. A Member who isn't an Admin is not shown the Admin pages' chords.
 */
export function shortcutSections({ admin }: { admin: boolean }) {
  const entries = (Object.entries(shortcuts) as [ShortcutId, Shortcut][]).filter(
    ([, shortcut]) => admin || !shortcut.admin,
  )
  return (Object.entries(scopes) as [Scope, string][])
    .map(([scope, heading]) => ({
      scope,
      heading,
      shortcuts: entries
        .filter(([, shortcut]) => shortcut.scope === scope)
        .map(([id, shortcut]) => ({ id, ...shortcut })),
    }))
    .filter(({ shortcuts }) => shortcuts.length > 0)
}
